import { useState } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { Button } from '../atoms/Button/Button';
import { Badge } from '../atoms/Badge/Badge';
import { Inline } from '../atoms/Inline/Inline';
import { Stack } from '../atoms/Stack/Stack';
import type { HistoryEntry, ParameterTarget } from '../../services/measurementService';
import { formatMeasuredAt, measuredAtDateTimeAttr } from '../../utils/measurementDate';
import { deriveStatus } from '../../hooks/useLatestMeasurements';
import { formatNumber, parameterLabel, parameterStatusKey, parameterStatusTone, sourceLabelKey } from './measurementHistoryUtils';
import { ParameterSwatch } from './ParameterSwatch';
import type { SeriesColourSlot } from './parameterColours';
import './MeasurementHistoryTable.css';

export const HISTORY_PAGE_SIZE = 50;

export interface MeasurementHistoryTableProps {
  /** Newest first. */
  entries: readonly HistoryEntry[];
  targets: Record<string, ParameterTarget>;
  caption: string;
  /** Series colour per parameter (the chart's colour key next to the parameter name). */
  colourSlots?: ReadonlyMap<string, SeriesColourSlot>;
  onViewEvidence: (entry: HistoryEntry) => void;
  onEdit: (entry: HistoryEntry) => void;
  onDelete: (entry: HistoryEntry) => void;
}

/**
 * The history as a table — also the accessible equivalent of the trend chart.
 * Manual / photo measurements can be corrected or deleted here; ICP values are
 * managed on the ICP analysis page. Shows HISTORY_PAGE_SIZE rows at a time.
 */
export function MeasurementHistoryTable({
  entries,
  targets,
  caption,
  colourSlots,
  onViewEvidence,
  onEdit,
  onDelete,
}: MeasurementHistoryTableProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language || 'de';
  const [visible, setVisible] = useState(HISTORY_PAGE_SIZE);
  const shown = entries.slice(0, visible);

  return (
    <Stack gap="4">
      <div className="measurement-history-table__frame">
        <table className="measurement-history-table">
          <caption className="measurement-history-table__caption">{caption}</caption>
          <thead>
            <tr>
              <th scope="col">{t('measurement.history.table.date')}</th>
              <th scope="col">{t('measurement.history.table.parameter')}</th>
              <th scope="col" className="measurement-history-table__numeric">{t('measurement.history.table.value')}</th>
              <th scope="col">{t('measurement.history.table.unit')}</th>
              <th scope="col">{t('measurement.history.table.target')}</th>
              <th scope="col">{t('measurement.history.table.source')}</th>
              <th scope="col">{t('measurement.history.table.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((entry) => {
              const label = parameterLabel(t, entry.parameterId, entry.parameterName);
              const status = deriveStatus(targets[entry.parameterId], entry.value);
              const statusKey = parameterStatusKey(status);
              // Date-only ICP values show their date alone (no spurious "02:00", #1653).
              const when = formatMeasuredAt(entry, language);
              const slot = colourSlots?.get(entry.parameterId);
              const rowName = `${label} ${formatNumber(entry.value, language)} ${entry.unit}, ${when}`.trim();
              return (
                <tr key={entry.id}>
                  <th scope="row" className="measurement-history-table__date">
                    <time dateTime={measuredAtDateTimeAttr(entry)}>{when}</time>
                  </th>
                  <td data-label={t('measurement.history.table.parameter')}>
                    {slot ? (
                      <span className="measurement-history-table__parameter">
                        <ParameterSwatch slot={slot} />
                        {label}
                      </span>
                    ) : (
                      label
                    )}
                  </td>
                  <td className="measurement-history-table__numeric" data-label={t('measurement.history.table.value')}>{formatNumber(entry.value, language)}</td>
                  <td data-label={t('measurement.history.table.unit')}>{entry.unit}</td>
                  <td data-label={t('measurement.history.table.target')}>
                    {statusKey === null ? (
                      <span className="measurement-history-table__muted">{t('measurement.history.target.none')}</span>
                    ) : (
                      <Badge tone={parameterStatusTone(status)}>{t(statusKey)}</Badge>
                    )}
                  </td>
                  <td data-label={t('measurement.history.table.source')}>
                    {t(sourceLabelKey(entry.source))}
                    {entry.provider && entry.source === 'icp' ? ` (${entry.provider})` : ''}
                  </td>
                  <td data-label={t('measurement.history.table.actions')}>
                    {entry.kind === 'measurement' ? (
                      <Inline gap="1" wrap>
                        {entry.hasEvidencePhoto && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onViewEvidence(entry)}
                            aria-label={t('measurement.history.actions.viewEvidenceFor', { name: rowName })}
                          >
                            {t('measurement.history.actions.viewEvidence')}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEdit(entry)}
                          aria-label={t('measurement.history.actions.editFor', { name: rowName })}
                        >
                          {t('measurement.history.actions.edit')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDelete(entry)}
                          aria-label={t('measurement.history.actions.deleteFor', { name: rowName })}
                        >
                          {t('measurement.history.actions.delete')}
                        </Button>
                      </Inline>
                    ) : (
                      <span className="measurement-history-table__muted">
                        {t(entry.kind === 'device' ? 'measurement.history.actions.deviceReadOnly' : 'measurement.history.actions.icpReadOnly')}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {entries.length > visible && (
        <Inline justify="center">
          <Button variant="secondary" onClick={() => setVisible((v) => v + HISTORY_PAGE_SIZE)}>
            {t('measurement.history.table.showMore', { count: Math.min(HISTORY_PAGE_SIZE, entries.length - visible) })}
          </Button>
        </Inline>
      )}
    </Stack>
  );
}

export default MeasurementHistoryTable;
